import React, { useState } from 'react';
import { AdminHireAPI } from '../../services/api';
import { REFUND_REASONS, CANCEL_REASONS, formatRand, randToCents, isLive, apiErrorMessage } from './charterUtils';

const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 outline-none';

/**
 * Refund and Cancel on a hire (spec 5.5, 3.5), shown to super admins only;
 * the server refuses anyone else with 403 anyway. Both send the hire version
 * this screen loaded (`expectedVersion`), so a double click or a stale
 * screen acts once at most. Neither is offered while money is in flight.
 */
const HireMoneyActions = ({ hire, onChanged }) => {
  // The row carries the claim at the top level even when `cancellation` is null.
  const s17Claimed = (hire.s17_5 || (hire.cancellation && hire.cancellation.s17_5)) === 'claimed';
  const [reason, setReason] = useState(s17Claimed ? 's17_5' : 'operator_no_show');
  const [amount, setAmount] = useState('');
  const [cancelReason, setCancelReason] = useState('operator_no_show');
  const [cancelNote, setCancelNote] = useState('');
  const [refundAll, setRefundAll] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const refundable = hire.refundableCents || 0;
  const s17 = reason === 's17_5';
  const amountCents = s17 ? refundable : randToCents(amount);
  const amountOk = amountCents !== null && amountCents > 0 && amountCents <= refundable;
  const reasons = REFUND_REASONS.filter((r) => r.value !== 's17_5' || s17Claimed);

  const run = async (call, done) => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const res = await call();
      setMessage(done(res.data.data));
      setAmount('');
      setCancelNote('');
    } catch (e) {
      setError(apiErrorMessage(e, 'That did not go through'));
    } finally {
      setBusy(false);
      await onChanged();
    }
  };

  const refund = () => {
    if (!amountOk) return;
    if (!window.confirm(`Refund ${formatRand(amountCents)} to the traveller's card?`)) return;
    run(
      () => AdminHireAPI.refund(hire._id, { amountCents, reason, expectedVersion: hire.version }),
      (data) => `Refund of ${formatRand(amountCents)}: ${data.outcome}.`,
    );
  };

  const cancel = () => {
    const what = refundAll ? 'and refund everything paid' : 'at the traveller\'s cancellation tier';
    if (!window.confirm(`Cancel ${hire.bookingCode} ${what}?`)) return;
    run(
      () => AdminHireAPI.cancel(hire._id, { refundAll, reason: cancelReason, note: cancelNote.trim() || undefined, expectedVersion: hire.version }),
      (data) => `Cancelled.${data.refundOutcome ? ` Refund ${data.refundOutcome}.` : ''}`,
    );
  };

  const outcome = (message || error) && (
    <p className={`lg:col-span-2 text-sm ${error ? 'text-red-600' : 'text-green-700'}`} role={error ? 'alert' : 'status'}>{error || message}</p>
  );

  if (hire.moneyInFlight) {
    // Keep the outcome of the action that just ran (an `unknown` refund leaves money in flight).
    return (
      <div className="space-y-2">
        {outcome}
        <p className="text-sm text-yellow-700">A payment on this hire is still being processed. Refund and cancel come back once it settles.</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="money-actions">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
        <h2 className="text-sm font-semibold text-gray-900">Refund</h2>
        <p className="text-xs text-gray-500 mb-3">Up to {formatRand(refundable)}, newest payment first. Zuttle returns its commission on the refunded part, pro rata.</p>
        {refundable <= 0 ? <p className="text-sm text-gray-500">Nothing left to refund.</p> : (
          <div className="space-y-3">
            <div>
              <label htmlFor="refund-reason" className="block text-xs font-medium text-gray-700 mb-1">Reason</label>
              <select id="refund-reason" className={inputClass} value={reason} onChange={(e) => setReason(e.target.value)}>
                {reasons.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="refund-amount" className="block text-xs font-medium text-gray-700 mb-1">Amount (rand)</label>
              <input
                id="refund-amount"
                inputMode="decimal"
                className={inputClass}
                value={s17 ? String(refundable / 100) : amount}
                disabled={s17}
                onChange={(e) => setAmount(e.target.value)}
              />
              {s17 && <p className="mt-1 text-xs text-gray-500">An s17(5) refund returns everything still held, once proof has been seen.</p>}
              {!s17 && amount && !amountOk && <p className="mt-1 text-xs text-red-600">Enter an amount above R 0 and at most {formatRand(refundable)}.</p>}
            </div>
            <button
              onClick={refund}
              disabled={busy || !amountOk}
              className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-medium disabled:opacity-50"
            >
              {s17 ? 'Refund the rest (s17(5))' : `Refund ${amountOk ? formatRand(amountCents) : ''}`.trim()}
            </button>
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
        <h2 className="text-sm font-semibold text-gray-900">Cancel</h2>
        {!isLive(hire) ? <p className="text-sm text-gray-500 mt-2">This hire is no longer live.</p> : (
          <div className="space-y-3 mt-2">
            <fieldset className="space-y-1 text-sm">
              <label className="flex items-center gap-2">
                <input type="radio" name="refund-mode" checked={refundAll} onChange={() => setRefundAll(true)} />
                Refund everything paid ({formatRand(hire.paidCents - hire.refundedCents)})
              </label>
              <label className="flex items-center gap-2">
                <input type="radio" name="refund-mode" checked={!refundAll} onChange={() => setRefundAll(false)} />
                Apply the traveller's cancellation tier
              </label>
            </fieldset>
            <div>
              <label htmlFor="cancel-reason" className="block text-xs font-medium text-gray-700 mb-1">Reason</label>
              <select id="cancel-reason" className={inputClass} value={cancelReason} onChange={(e) => setCancelReason(e.target.value)}>
                {CANCEL_REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="cancel-note" className="block text-xs font-medium text-gray-700 mb-1">Note (optional)</label>
              <input id="cancel-note" className={inputClass} value={cancelNote} onChange={(e) => setCancelNote(e.target.value)} />
            </div>
            <button onClick={cancel} disabled={busy} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium disabled:opacity-50">
              Cancel this hire
            </button>
          </div>
        )}
      </div>

      {outcome}
    </div>
  );
};

export default HireMoneyActions;
